import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import ChatWidget from '../components/site/ChatWidget';
import Seo, { SITE_URL, breadcrumbLd } from '../components/Seo';
import { SERVICES, serviceBySlug } from '../lib/servicesData';
import { fetchCaseStudies } from '../lib/api';
import { useI18n } from '../i18n';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Check, ArrowRight, ArrowLeft, ShieldCheck } from 'lucide-react';

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const svc = serviceBySlug(slug);
  const [related, setRelated] = React.useState([]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    if (!svc?.related?.length) { setRelated([]); return; }
    fetchCaseStudies()
      .then((all) => setRelated(svc.related.map((s) => all.find((c) => c.slug === s)).filter(Boolean)))
      .catch(() => setRelated([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!svc) {
    return (
      <div className="min-h-screen bg-white"><Navbar />
        <div className="max-w-3xl mx-auto px-5 py-40 text-center">
          <h1 className="text-3xl font-extrabold">Service not found</h1>
          <Button onClick={() => nav('/#services')} className="mt-6 bg-orange-500 hover:bg-orange-600">All services</Button>
        </div><Footer /></div>
    );
  }

  const title = t(`services.items.${svc.id}.title`);
  const subtitle = t(`services.items.${svc.id}.subtitle`);
  const childServices = t(`services.items.${svc.id}.list`) || [];
  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 6);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Seo
        title={`${svc.seoTitle} | Hipro Technologies`}
        description={svc.seoDescription}
        path={`/services/${svc.slug}`}
        image={svc.image}
        jsonLd={[
          breadcrumbLd([{ name: 'Home', path: '/' }, { name: 'Services', path: '/#services' }, { name: svc.seoTitle, path: `/services/${svc.slug}` }]),
          {
            '@context': 'https://schema.org', '@type': 'Service',
            name: svc.seoTitle, description: svc.seoDescription, url: `${SITE_URL}/services/${svc.slug}`,
            provider: { '@type': 'Organization', name: 'Hipro Technologies', url: SITE_URL },
            areaServed: ['IN', 'US', 'GB', 'IT', 'SG'],
          },
          {
            '@context': 'https://schema.org', '@type': 'FAQPage',
            mainEntity: svc.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
          },
        ]}
      />
      <Navbar />

      <section className="pt-28 pb-12 bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Link to="/#services" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-orange-400"><ArrowLeft className="h-4 w-4" /> All services</Link>
            <span className="block mt-4 text-xs font-semibold uppercase tracking-widest text-orange-400">{t(`services.items.${svc.id}.tag`)}</span>
            <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold" data-testid="service-detail-title">{title}</h1>
            <p className="mt-4 text-slate-300">{subtitle}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button onClick={() => nav('/book-meeting')} data-testid="service-book-btn" className="bg-orange-500 hover:bg-orange-600 h-11 px-6">Book a free consultation <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
              <Button onClick={() => nav('/pricing')} variant="outline" className="h-11 px-6 border-white/20 bg-transparent text-white hover:bg-white/10">See pricing</Button>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-white/10">
            <img src={svc.image} alt={title} className="w-full aspect-[16/10] object-cover" />
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-5">
            {svc.intro.map((p, i) => <p key={i} className="text-slate-700 leading-relaxed">{p}</p>)}
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {svc.benefits.map((b) => (
                <div key={b.t} className="rounded-xl border border-slate-200 p-5 hover:border-orange-300 transition-colors">
                  <div className="flex items-center gap-2 font-bold"><ShieldCheck className="h-4 w-4 text-orange-500" /> {b.t}</div>
                  <p className="mt-1.5 text-sm text-slate-600">{b.d}</p>
                </div>
              ))}
            </div>
          </div>
          <aside className="rounded-2xl border border-slate-200 bg-slate-50 p-6 h-fit">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t('services.childServices')}</div>
            <ul className="mt-3 space-y-2">
              {childServices.map((it) => (
                <li key={it} className="flex items-center gap-2 text-sm text-slate-700"><Check className="h-4 w-4 text-emerald-500 shrink-0" /> {it}</li>
              ))}
            </ul>
            <Button onClick={() => nav('/book-meeting')} className="mt-6 w-full bg-slate-900 hover:bg-slate-800">Get a free estimate</Button>
          </aside>
        </div>
      </section>

      <section className="py-14 bg-slate-50">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-center">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="mt-8" data-testid="service-faqs">
            {svc.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-xl border px-5 mb-3">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          {related.length > 0 && (
            <div className="mb-14" data-testid="service-related-projects">
              <h2 className="text-xl font-extrabold">Proof it works — related projects</h2>
              <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {related.map((c) => (
                  <Link key={c.slug} to={`/portfolio/${c.slug}`} data-testid={`related-project-${c.slug}`} className="group rounded-2xl overflow-hidden border border-slate-200 hover:shadow-xl hover:-translate-y-0.5 transition-all">
                    <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                      <img src={c.hero_image} alt={c.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
                    </div>
                    <div className="p-5">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-orange-600">{c.industry}</div>
                      <div className="mt-1.5 font-bold text-slate-900 group-hover:text-orange-600 transition-colors leading-snug">{c.title}</div>
                      <div className="mt-2 text-sm text-slate-600 line-clamp-2">{c.outcome_headline}</div>
                      <div className="mt-3 text-sm font-semibold text-orange-600 inline-flex items-center gap-1">View case study <ArrowRight className="h-3.5 w-3.5" /></div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
          <h2 className="text-xl font-extrabold">Explore other services</h2>
          <div className="mt-5 flex flex-wrap gap-2">
            {others.map((o) => (
              <Link key={o.slug} to={`/services/${o.slug}`} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-orange-400 hover:text-orange-600 transition-colors">
                {t(`services.items.${o.id}.title`)}
              </Link>
            ))}
          </div>
          <div className="mt-10 rounded-2xl bg-slate-950 text-white p-8 text-center">
            <h3 className="text-xl font-extrabold">Ready to start?</h3>
            <p className="mt-2 text-slate-300 text-sm">Free 30-minute consultation — detailed estimate within 24 hours.</p>
            <Button onClick={() => nav('/book-meeting')} data-testid="service-cta-btn" className="mt-5 bg-orange-500 hover:bg-orange-600 rounded-full px-6">Book a free call</Button>
          </div>
        </div>
      </section>
      <Footer />
      <ChatWidget />
    </div>
  );
}
